/**
 * Built-in completion provider for selector fields (WP2).
 *
 * The CnD selector language is the Forge/Alloy relational-expression sublanguage
 * evaluated by `simple-graph-query` (see `src/evaluators/data/sgq-evaluator.ts`).
 * The keyword/operator set below is derived from that grammar's lexer
 * (`simple-graph-query/dist/forge-antlr/ForgeLexer.d.ts`) restricted to the
 * tokens that can appear inside a *selector expression* (module/declaration
 * tokens like `sig`, `pred`, `run`, `check` are intentionally excluded), cross
 * checked against the old highlighter's keyword set in
 * `src/components/NoCodeView/Selectors/SelectorInput.tsx`.
 *
 * Three entry points:
 *  - `getSelectorKeywordCompletions()` — language keywords/operators/snippets,
 *    independent of any instance.
 *  - `getDomainCompletions(domain)` — types/relations/atoms from a DomainSchema.
 *  - `createBuiltinCompletionSource(domain?)` — a merged, prefix-filtered
 *    `(prefix) => Completion[]` the UI calls on each keystroke; this is also
 *    what an integrator merges with `assistant.complete()` results.
 *
 * This module is framework-agnostic — no React.
 */

import type { Completion } from './assistant';
import type { DomainSchema } from './domain-schema';

/**
 * Maximum number of atom completions emitted from a domain. Instances can hold
 * thousands of atoms; offering all of them would flood the popup and slow
 * filtering. The cap keeps the built-in source responsive; an integrator that
 * needs more can supply an `assistant.complete()` that returns instance-aware
 * results. Tunable; logged-friendly constant for easy discovery.
 */
export const MAX_ATOM_COMPLETIONS = 200;

// ---- selector-language keyword/operator/snippet tables -------------------

/**
 * Built-in atom/relation constants of the selector language. These are valid
 * selector references that are NOT user domain types, so they live here (and
 * are exempt from domain validation).
 */
const BUILTIN_VALUES: readonly string[] = [
  'univ', // the universal set
  'none', // the empty set
  'iden', // the identity relation
  'Int', // built-in integers
];

/**
 * Quantifier / multiplicity keywords usable inside selector expressions
 * (`all x : S | ...`, `some S`, `lone f`, `set ...`, etc.). Derived from the
 * Forge lexer's ALL/SOME/ONE/LONE/NO/SET/SUM/LET/DISJ tokens.
 */
const QUANTIFIER_KEYWORDS: readonly string[] = [
  'all',
  'some',
  'no',
  'one',
  'lone',
  'set',
  'sum',
  'let',
  'disj',
];

/**
 * Word-form logical / comparison operators (Forge AND/OR/NOT/IN/IFF/IMP/ELSE
 * and the `not in` form). Symbolic operators are offered separately as
 * snippets so the popup can show a hint.
 */
const OPERATOR_KEYWORDS: readonly string[] = [
  'in',
  'and',
  'or',
  'not',
  'implies',
  'iff',
  'else',
];

/**
 * Symbolic operators offered as snippets (insert the operator, show a hint).
 * Mirrors the highlighter's operator set: join/product/closure/union/etc.
 */
const OPERATOR_SNIPPETS: ReadonlyArray<{ insert: string; detail: string }> = [
  { insert: '.', detail: 'join' },
  { insert: '->', detail: 'product' },
  { insert: '+', detail: 'union' },
  { insert: '&', detail: 'intersection' },
  { insert: '-', detail: 'difference' },
  { insert: '~', detail: 'transpose' },
  { insert: '^', detail: 'transitive closure' },
  { insert: '*', detail: 'reflexive-transitive closure' },
  { insert: '#', detail: 'cardinality' },
];

let cachedKeywordCompletions: Completion[] | undefined;

/**
 * Selector-language keyword/operator/snippet completions. Independent of any
 * instance; cached after first build (the list is constant).
 */
export function getSelectorKeywordCompletions(): Completion[] {
  if (cachedKeywordCompletions) {
    // Return a copy so callers can freely sort/mutate their working list.
    return cachedKeywordCompletions.map((c) => ({ ...c }));
  }

  const out: Completion[] = [];

  for (const value of BUILTIN_VALUES) {
    out.push({ label: value, kind: 'keyword', detail: 'built-in' });
  }
  for (const kw of QUANTIFIER_KEYWORDS) {
    out.push({ label: kw, kind: 'keyword', detail: 'quantifier' });
  }
  for (const kw of OPERATOR_KEYWORDS) {
    out.push({ label: kw, kind: 'keyword', detail: 'operator' });
  }
  for (const op of OPERATOR_SNIPPETS) {
    out.push({
      label: op.insert,
      insertText: op.insert,
      kind: 'snippet',
      detail: op.detail,
    });
  }

  cachedKeywordCompletions = out;
  return out.map((c) => ({ ...c }));
}

// ---- domain completions --------------------------------------------------

/**
 * Completions for the types, relations, and atoms in a `DomainSchema`. Atoms
 * are capped at `MAX_ATOM_COMPLETIONS` to keep the popup responsive on large
 * instances; the cap is applied across the whole schema (not per type).
 */
export function getDomainCompletions(domain: DomainSchema): Completion[] {
  const out: Completion[] = [];

  for (const type of domain.types) {
    out.push({
      label: type.name,
      kind: 'type',
      detail: 'type',
    });
  }

  for (const relation of domain.relations) {
    out.push({
      label: relation.name,
      kind: 'relation',
      detail:
        relation.arity !== undefined
          ? `relation · arity ${relation.arity}`
          : 'relation',
    });
  }

  // Atoms, capped. Iterate types in order and stop once the cap is reached so
  // the offered atoms are deterministic. Skip atoms whose name collides with a
  // type name (the type completion already covers it).
  const typeNames = new Set(domain.types.map((t) => t.name));
  const atomSeen = new Set<string>();
  let atomCount = 0;
  for (const type of domain.types) {
    if (atomCount >= MAX_ATOM_COMPLETIONS) {
      break;
    }
    for (const atom of type.atoms) {
      if (atomCount >= MAX_ATOM_COMPLETIONS) {
        break;
      }
      if (atomSeen.has(atom) || typeNames.has(atom)) {
        continue;
      }
      atomSeen.add(atom);
      out.push({ label: atom, kind: 'atom', detail: `atom · ${type.name}` });
      atomCount += 1;
    }
  }

  return out;
}

// ---- merged, prefix-filtered source --------------------------------------

/** Rank: 0 = case-insensitive prefix match, 1 = substring match, 2 = no match. */
function matchRank(label: string, prefix: string): number {
  if (prefix.length === 0) {
    return 0;
  }
  const haystack = label.toLowerCase();
  const needle = prefix.toLowerCase();
  if (haystack.startsWith(needle)) {
    return 0;
  }
  if (haystack.includes(needle)) {
    return 1;
  }
  return 2;
}

/**
 * Priority of a completion kind when two entries match equally well. Domain
 * entries (types/relations/atoms) sort before language keywords/snippets, per
 * the design ("keywords after domain entries on equal match quality").
 */
function kindPriority(kind: Completion['kind']): number {
  switch (kind) {
    case 'type':
      return 0;
    case 'relation':
      return 1;
    case 'atom':
      return 2;
    case 'keyword':
      return 3;
    case 'snippet':
      return 4;
    default:
      return 5;
  }
}

/** Dedupe key for a completion: label + insertText (so `-` op vs `-` differ if needed). */
function dedupeKey(c: Completion): string {
  return `${c.label} ${c.insertText ?? c.label}`;
}

/**
 * Build the built-in completion source: a `(prefix) => Completion[]` that merges
 * domain completions (when a schema is present) with selector keywords, filters
 * by prefix (case-insensitive prefix match, then substring as a fallback),
 * dedupes, and orders domain entries before keywords on equal match quality.
 *
 * This is the function the UI layer calls; the WP4 integrator merges its result
 * with `assistant.complete()` output (see `mergeCompletions`).
 */
export function createBuiltinCompletionSource(
  domain?: DomainSchema,
): (prefix: string) => Completion[] {
  // Pre-compute the unfiltered, deduped pool once per source so each keystroke
  // only filters + sorts.
  const pool: Completion[] = [];
  if (domain) {
    pool.push(...getDomainCompletions(domain));
  }
  pool.push(...getSelectorKeywordCompletions());

  const deduped = dedupeCompletions(pool);

  return (prefix: string): Completion[] => {
    const scored: Array<{ c: Completion; rank: number; idx: number }> = [];
    deduped.forEach((c, idx) => {
      const rank = matchRank(c.label, prefix);
      if (rank < 2) {
        scored.push({ c, rank, idx });
      }
    });

    scored.sort((a, b) => {
      if (a.rank !== b.rank) {
        return a.rank - b.rank;
      }
      const pa = kindPriority(a.c.kind);
      const pb = kindPriority(b.c.kind);
      if (pa !== pb) {
        return pa - pb;
      }
      // Stable-ish: shorter labels first, then original order.
      if (a.c.label.length !== b.c.label.length) {
        return a.c.label.length - b.c.label.length;
      }
      return a.idx - b.idx;
    });

    return scored.map((s) => s.c);
  };
}

/**
 * Merge and dedupe completion lists, preserving first-seen order. Useful for the
 * UI layer to fold `assistant.complete()` results into the built-in source's
 * output (assistant results passed first win on ties). Pure helper.
 */
export function mergeCompletions(
  ...lists: ReadonlyArray<readonly Completion[]>
): Completion[] {
  const flat: Completion[] = [];
  for (const list of lists) {
    for (const c of list) {
      flat.push(c);
    }
  }
  return dedupeCompletions(flat);
}

/** Dedupe completions on (label, insertText), preserving first-seen order. */
function dedupeCompletions(list: readonly Completion[]): Completion[] {
  const seen = new Set<string>();
  const out: Completion[] = [];
  for (const c of list) {
    const key = dedupeKey(c);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(c);
  }
  return out;
}
